import { z } from "zod";
import {
  zOutlineCharacter,
  zOutlinePlace,
  zOutlineItem,
  zStoryOutline,
  zStoryReferenceImages,
  type StoryOutline,
} from "./storyOutline";
import { zWorldGenerationRef } from "./worlds";
import { zStoryFraming } from "./storyFraming";

/** A reviewable creative brief. Templates are prompts, not separate generation products. */
export const zStoryProposal = z
  .object({
    version: z.literal(1).default(1),
    idea: z.string().min(1).max(8000),
    title: z.string().min(1).max(80),
    logline: z.string().min(1).max(1000),
    format: z.enum(["spoken", "musical"]),
    tone: z.string().max(1000),
    visualStyle: z.string().max(1000),
    beats: z.array(z.string().min(1).max(1200)).min(1).max(8),
    reply: z.object({ question: z.string().max(300), suggestions: z.array(z.string().max(160)).max(4) }),
    cast: z.array(zOutlineCharacter).min(1).max(6),
    places: z.array(zOutlinePlace).max(3),
    items: z.array(zOutlineItem).max(6),
    referenceImages: zStoryReferenceImages.optional(),
    world: zWorldGenerationRef.optional(),
    framing: zStoryFraming.optional(),
    musicalOutline: zStoryOutline.optional(),
    storyId: z.string().max(100).optional(),
  })
  .strip();
export type StoryProposal = z.infer<typeof zStoryProposal>;
export type StoryAssetOutline = Omit<StoryOutline, "song"> & { song?: StoryOutline["song"] };
export const storyProposalArtworkReady = (proposal: StoryProposal): boolean =>
  proposal.cast.every((entry) => !!entry.castId) &&
  proposal.places.every((entry) => !!entry.backdropId) &&
  proposal.items.every((entry) => !!entry.itemId);

/** Recasting changes the selected performer while preserving the proposal role's stable id. */
export function recastProposalRole(
  proposal: StoryProposal,
  roleId: string,
  choice?: { animalId: string; accountId: string; personaId: string; worldEntityId?: string }
): StoryProposal {
  const cast = proposal.cast.map((entry) => {
    if (entry.id !== roleId) return entry;
    const {
      castId: _cast,
      existing: _existing,
      worldEntityId: _world,
      pendingSheet: _sheet,
      copyCastIds: _copies,
      copyRepresentationIds: _representations,
      ...role
    } = entry;
    return {
      ...role,
      castingLocked: true,
      ...(choice
        ? {
            existing: { animalId: choice.animalId, accountId: choice.accountId, personaId: choice.personaId },
            ...(choice.worldEntityId ? { worldEntityId: choice.worldEntityId } : {}),
            referenceImageIndexes: [],
          }
        : {}),
    };
  });
  return {
    ...proposal,
    cast,
    ...(proposal.musicalOutline ? { musicalOutline: { ...proposal.musicalOutline, cast } } : {}),
  };
}

export const STORY_INTENTS = [
  {
    label: "A new story",
    prompt:
      "Start a short adventure with a satisfying first scene and room for the viewer to decide what happens next.",
  },
  {
    label: "A gift for a friend",
    prompt:
      "Bring this artwork to life as a personal message and a short story for a friend. Preserve the art's character and invite my friend to continue the adventure.",
  },
  {
    label: "Singing telegram",
    prompt:
      "Create a single scene of an original musical performance delivering my message. End with an invitation to reply with another scene.",
  },
  {
    label: "Explore a feeling",
    prompt:
      "Create a gentle spoken scene that helps the character explore how they feel. Listen with curiosity, avoid assuming the answer, and invite the viewer to help decide what happens next.",
  },
] as const;
